function initHeader(header) {
  if (!header || header.dataset.ready === 'true') return;
  header.dataset.ready = 'true';

  const controller = new AbortController();
  const listenerOptions = { signal: controller.signal };
  const searchPanel = header.querySelector('.editorial-header__search');
  const searchButtons = [...header.querySelectorAll('.editorial-header__search-toggle')];
  const drawer = header.querySelector('.editorial-header__drawer');
  const drawerButton = header.querySelector('.editorial-header__menu-toggle');
  const drawerCloseButtons = [...header.querySelectorAll('[data-drawer-close]')];
  const mobileMenu = drawer.querySelector('editorial-mobile-menu');
  const drawerInertNodes = new Set();
  const drawerOpenLabel = drawerButton.getAttribute('aria-label');
  const drawerCloseLabel = drawerCloseButtons[0].getAttribute('aria-label');
  drawerButton.id = drawerButton.id || `${drawer.id}-toggle`;

  const isolateDrawer = () => {
    const isolateBranch = node => {
      if (node === drawerButton || node.inert || node.matches('script, style, link')) return;
      // Keep the toggle available while isolating the other header controls.
      if (node.contains(drawerButton)) {
        [...node.children].forEach(isolateBranch);
        return;
      }
      node.inert = true;
      drawerInertNodes.add(node);
    };
    let branch = drawer;
    while (branch.parentElement) {
      for (const sibling of branch.parentElement.children) {
        if (sibling !== branch) isolateBranch(sibling);
      }
      branch = branch.parentElement;
      if (branch === document.body) break;
    }
  };

  const megaButtons = [...header.querySelectorAll('[data-mega-trigger]')];
  const megaPanels = [...header.querySelectorAll('.editorial-header__mega')];
  const navigation = header.querySelector('.editorial-header__nav');
  const navigationInner = header.querySelector('.editorial-header__nav-inner');
  let activeTrigger = null;
  let navigationIsCondensed = false;
  let navigationFrame = 0;
  let deferredFocusFrame = 0;
  let interactionVersion = 0;

  const drawerFocusables = () => [drawerButton, ...drawer.querySelectorAll('a, button, input, summary, [tabindex]:not([tabindex="-1"])')]
    .filter(element => !element.hasAttribute('disabled') && !element.closest('[inert], [aria-hidden="true"]') && element.getClientRects().length > 0);

  const cancelDeferredFocus = () => {
    interactionVersion += 1;
    if (!deferredFocusFrame) return;
    cancelAnimationFrame(deferredFocusFrame);
    deferredFocusFrame = 0;
  };

  const focusAfterRender = callback => {
    if (deferredFocusFrame) cancelAnimationFrame(deferredFocusFrame);
    const version = interactionVersion;
    deferredFocusFrame = requestAnimationFrame(() => {
      deferredFocusFrame = 0;
      if (version !== interactionVersion || controller.signal.aborted) return;
      callback();
    });
  };

  const closeAll = (restoreFocus = false) => {
    cancelDeferredFocus();
    drawerInertNodes.forEach(node => { node.inert = false; });
    drawerInertNodes.clear();
    searchPanel.hidden = true;
    searchPanel.querySelector('predictive-search')?.close?.();
    searchButtons.forEach(button => button.setAttribute('aria-expanded', 'false'));
    mobileMenu.close();
    drawer.hidden = true;
    drawer.removeAttribute('aria-owns');
    drawerButton.setAttribute('aria-expanded', 'false');
    drawerButton.setAttribute('aria-label', drawerOpenLabel);
    megaPanels.forEach(panel => { panel.hidden = true; });
    megaButtons.forEach(button => button.setAttribute('aria-expanded', 'false'));
    document.documentElement.classList.remove('editorial-drawer-open');
    if (restoreFocus && activeTrigger) activeTrigger.focus();
    activeTrigger = null;
  };

  searchButtons.forEach(button => button.addEventListener('click', () => {
    const shouldOpen = searchPanel.hidden;
    closeAll();
    if (!shouldOpen) return;
    activeTrigger = button;
    searchPanel.hidden = false;
    searchButtons.forEach(item => item.setAttribute('aria-expanded', 'true'));
    focusAfterRender(() => {
      if (!searchPanel.hidden) searchPanel.querySelector('input')?.focus();
    });
  }, listenerOptions));

  const openDrawer = () => {
    if (mobileMenu.pendingClose || !drawer.hidden) return;
    closeAll();
    activeTrigger = drawerButton;
    drawer.hidden = false;
    drawer.setAttribute('aria-owns', drawerButton.id);
    drawerButton.setAttribute('aria-expanded', 'true');
    drawerButton.setAttribute('aria-label', drawerCloseLabel);
    document.documentElement.classList.add('editorial-drawer-open');
    isolateDrawer();
    syncStickyHeaderHeight();
    mobileMenu.open();
  };
  drawerButton.addEventListener('click', () => {
    if (drawer.hidden) openDrawer();
    else closeAll(true);
  }, listenerOptions);
  header.addEventListener('mobile-menu:open', openDrawer, listenerOptions);
  header.addEventListener('mobile-menu:close', () => closeAll(true), listenerOptions);

  drawerCloseButtons.forEach(button => button.addEventListener('click', () => closeAll(true), listenerOptions));
  controller.signal.addEventListener('abort', cancelDeferredFocus, { once: true });

  megaButtons.forEach(button => button.addEventListener('click', () => {
    const panel = header.querySelector(`#${CSS.escape(button.dataset.megaTrigger)}`);
    const shouldOpen = panel.hidden;
    closeAll();
    if (!shouldOpen) return;
    activeTrigger = button;
    panel.hidden = false;
    button.setAttribute('aria-expanded', 'true');
  }, listenerOptions));

  document.addEventListener('keydown', event => {
    if (event.key === 'Escape' && activeTrigger) {
      closeAll(true);
      return;
    }
    if (event.key !== 'Tab' || drawer.hidden) return;
    const focusables = drawerFocusables();
    if (focusables.length === 0) {
      event.preventDefault();
      drawerButton.focus();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && (document.activeElement === first || !focusables.includes(document.activeElement))) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }, listenerOptions);

  document.addEventListener('pointerdown', event => {
    if (activeTrigger && !header.contains(event.target)) closeAll(true);
  }, listenerOptions);
  window.addEventListener('resize', () => {
    if (window.innerWidth > 980 && !navigationIsCondensed && !drawer.hidden) closeAll();
  }, listenerOptions);

  const updateNavigationMode = () => {
    navigationFrame = 0;
    if (!navigation || controller.signal.aborted) return;

    const shouldCondense = window.innerWidth > 980 && navigationInner.scrollWidth > navigationInner.clientWidth + 1;
    if (shouldCondense === navigationIsCondensed) return;

    navigationIsCondensed = shouldCondense;
    header.classList.toggle('is-navigation-condensed', shouldCondense);
    navigation.setAttribute('aria-hidden', String(shouldCondense));
    if (!drawer.hidden || megaPanels.some(panel => !panel.hidden)) closeAll();
  };

  const scheduleNavigationModeUpdate = () => {
    if (navigationFrame || controller.signal.aborted) return;
    navigationFrame = requestAnimationFrame(updateNavigationMode);
  };

  scheduleNavigationModeUpdate();
  window.addEventListener('resize', scheduleNavigationModeUpdate, listenerOptions);
  document.fonts?.ready.then(scheduleNavigationModeUpdate);
  controller.signal.addEventListener('abort', () => cancelAnimationFrame(navigationFrame), { once: true });

  const syncStickyHeaderHeight = () => {
    const bounds = header.getBoundingClientRect();
    const height = Math.ceil(bounds.height);
    const viewportBottom = window.visualViewport ? window.visualViewport.height + window.visualViewport.offsetTop : window.innerHeight;
    header.style.setProperty('--eh-drawer-height', `${Math.max(0, viewportBottom - bounds.bottom)}px`);
    header.style.setProperty('--eh-header-height', `${height}px`);
    document.documentElement.style.setProperty('--editorial-sticky-header-height', `${header.dataset.sticky === 'true' ? height : 0}px`);
  };
  syncStickyHeaderHeight();
  window.addEventListener('resize', syncStickyHeaderHeight, listenerOptions);
  window.visualViewport?.addEventListener('resize', syncStickyHeaderHeight, listenerOptions);
  window.visualViewport?.addEventListener('scroll', syncStickyHeaderHeight, listenerOptions);
  const headerResizeObserver = 'ResizeObserver' in window ? new ResizeObserver(() => {
    syncStickyHeaderHeight();
    scheduleNavigationModeUpdate();
  }) : null;
  headerResizeObserver?.observe(header);
  controller.signal.addEventListener('abort', () => {
    headerResizeObserver?.disconnect();
    header.style.removeProperty('--eh-header-height');
    header.style.removeProperty('--eh-drawer-height');
    document.documentElement.style.removeProperty('--editorial-sticky-header-height');
  }, { once: true });

  if (header.dataset.sticky === 'true') {
    let isCompact = header.classList.contains('is-compact');
    let compactFrame = 0;
    const collapseAt = 64;
    const expandAt = 12;
    const updateCompact = () => {
      compactFrame = 0;
      const scrollTop = Math.max(0, window.scrollY);
      const nextCompact = isCompact ? scrollTop > expandAt : scrollTop >= collapseAt;
      if (nextCompact === isCompact) return;
      isCompact = nextCompact;
      header.classList.toggle('is-compact', isCompact);
    };
    const requestCompactUpdate = () => {
      if (compactFrame) return;
      compactFrame = requestAnimationFrame(updateCompact);
    };
    updateCompact();
    window.addEventListener('scroll', requestCompactUpdate, { passive: true, signal: controller.signal });
    controller.signal.addEventListener('abort', () => cancelAnimationFrame(compactFrame), { once: true });
  }

  document.addEventListener('shopify:section:unload', event => {
    if (event.target.contains(header)) { closeAll(); controller.abort(); delete header.dataset.ready; }
  }, listenerOptions);
  if (mobileMenu.wantsOpen) openDrawer();
}

// ES modules execute once; each section instance owns its abortable listeners.
customElements.whenDefined('editorial-mobile-menu').then(() => {
  document.querySelectorAll('[data-editorial-header]').forEach(initHeader);
  document.addEventListener('shopify:section:load', event => {
    event.target.querySelectorAll('[data-editorial-header]').forEach(initHeader);
  });
});
